import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { INVITE } from '@/constants/testIds';
import { Calendar, MapPin, Users, Sparkles, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

function formatDateRange(start, end) {
    try {
        const s = parseISO(start), e = parseISO(end);
        if (s.getFullYear() === e.getFullYear()) {
            return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
        }
        return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
    } catch { return `${start} – ${end}`; }
}

export default function InvitePage() {
    const { inviteCode } = useParams();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, preview: null, error: null });
    const [accepting, setAccepting] = useState(false);

    const fetchPreview = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/by-invite/${inviteCode}`);
            setState({ loading: false, preview: data, error: null });
        } catch (err) {
            setState({ loading: false, preview: null, error: err?.response?.data?.detail || 'Invite not found' });
        }
    }, [inviteCode]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            // Stash the destination and bounce to login.
            navigate(`/login?redirect=${encodeURIComponent(`/join/${inviteCode}`)}`, { replace: true });
            return;
        }
        fetchPreview();
    }, [authLoading, user, inviteCode, navigate, fetchPreview]);

    const accept = async () => {
        setAccepting(true);
        try {
            const { data } = await api.post(`/trips/join/${inviteCode}`);
            toast.success("You're in! Welcome to the trip.");
            navigate(`/trips/${data.trip.trip_id}?welcome=1`, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not join the trip');
            setAccepting(false);
        }
    };

    if (authLoading || state.loading) {
        return (
            <div className="min-h-screen bg-[var(--ns-bg)]">
                <Header />
                <div data-testid={INVITE.loading} className="flex items-center justify-center py-32">
                    <div className="w-10 h-10 rounded-full border-2 border-[var(--ns-border)] border-t-[var(--ns-clay)] animate-spin" />
                </div>
            </div>
        );
    }

    if (state.error) {
        return (
            <div className="min-h-screen bg-[var(--ns-bg)]">
                <Header />
                <div data-testid={INVITE.notFound} className="max-w-xl mx-auto text-center px-4 py-20">
                    <h1 className="font-display text-3xl font-semibold tracking-tight">Invite not found</h1>
                    <p className="text-[var(--ns-text2)] mt-3">This invite link is invalid or has expired. Ask the trip owner for a fresh link.</p>
                    <Button
                        onClick={() => navigate('/dashboard')}
                        className="mt-6 rounded-full h-11 px-6 text-white"
                        style={{ background: 'var(--ns-navy)' }}
                    >
                        Back to dashboard
                    </Button>
                </div>
            </div>
        );
    }

    const { trip, owner, already_participant } = state.preview;

    return (
        <div className="min-h-screen bg-[var(--ns-bg)] flex flex-col" data-testid={INVITE.root}>
            <Header />
            <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
                <div className="text-center mb-10">
                    <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[var(--ns-clay)] font-medium">
                        <Sparkles size={12} /> You&apos;re invited
                    </span>
                    <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 text-[var(--ns-text)]">
                        Join <span className="italic">{trip.name}</span>
                    </h1>
                    {owner && (
                        <p className="text-[var(--ns-text2)] mt-3 inline-flex items-center gap-2">
                            <UserAvatar name={owner.name} picture={owner.picture} size={24} />
                            <span data-testid={INVITE.ownerName}>{owner.name}</span> wants you along.
                        </p>
                    )}
                </div>

                <div className="ns-card p-6 sm:p-8 space-y-5">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Destination</div>
                        <div data-testid={INVITE.tripDestination} className="font-display text-xl font-semibold mt-1 flex items-center gap-2">
                            <MapPin size={16} className="text-[var(--ns-clay)]" /> {trip.destination}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Dates</div>
                        <div data-testid={INVITE.tripDates} className="font-display text-xl font-semibold mt-1 flex items-center gap-2">
                            <Calendar size={16} className="text-[var(--ns-clay)]" /> {formatDateRange(trip.start_date, trip.end_date)}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Travelers</div>
                        <div className="font-display text-xl font-semibold mt-1 flex items-center gap-2">
                            <Users size={16} className="text-[var(--ns-clay)]" /> {trip.participants?.length || 0}
                        </div>
                        <div className="flex -space-x-2 mt-2">
                            {(trip.participants || []).slice(0, 8).map(p => (
                                <UserAvatar key={p.user_id} name={p.name} picture={p.picture} size={28} ring />
                            ))}
                        </div>
                    </div>
                </div>

                {already_participant ? (
                    <div className="mt-8 text-center">
                        <p data-testid={INVITE.alreadyMember} className="text-[var(--ns-text2)] mb-4">You&apos;re already part of this trip.</p>
                        <Button
                            onClick={() => navigate(`/trips/${trip.trip_id}`)}
                            data-testid={INVITE.acceptButton}
                            className="rounded-full h-12 px-7 text-white font-medium"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            Open trip <ChevronRight size={16} className="ml-1" />
                        </Button>
                    </div>
                ) : (
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                            onClick={accept}
                            disabled={accepting}
                            data-testid={INVITE.acceptButton}
                            className="rounded-full h-12 px-7 text-white font-medium order-1 sm:order-2"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            {accepting ? 'Joining…' : 'Accept invite'}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/dashboard')}
                            data-testid={INVITE.declineButton}
                            className="rounded-full h-12 px-7 border-[var(--ns-border)] order-2 sm:order-1"
                        >
                            Decline
                        </Button>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
